/**
 * useFunnelDrag — hook for drag-and-drop node positioning on the funnel canvas.
 * Handles mouse/touch events and updates node positions.
 */
import { useState, useCallback, useRef } from "react";
import type { FunnelNodeData } from "./funnel-types";

interface DragState {
  nodeId: string;
  startX: number;
  startY: number;
  originalX: number;
  originalY: number;
}

interface UseFunnelDragResult {
  isDragging: boolean;
  dragNodeId: string | null;
  onPointerDown: (nodeId: string, event: React.PointerEvent) => void;
  onPointerMove: (event: React.PointerEvent) => void;
  onPointerUp: () => void;
}

export function useFunnelDrag(
  nodes: FunnelNodeData[],
  onUpdatePosition: (nodeId: string, position: { x: number; y: number }) => void
): UseFunnelDragResult {
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragState | null>(null);

  const onPointerDown = useCallback(
    (nodeId: string, event: React.PointerEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      dragRef.current = {
        nodeId,
        startX: event.clientX,
        startY: event.clientY,
        originalX: node.position.x,
        originalY: node.position.y,
      };
      setIsDragging(true);
      (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    },
    [nodes]
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;

      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      const newX = Math.max(0, drag.originalX + dx);
      const newY = Math.max(0, drag.originalY + dy);

      onUpdatePosition(drag.nodeId, { x: newX, y: newY });
    },
    [onUpdatePosition]
  );

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  return {
    isDragging,
    dragNodeId: dragRef.current?.nodeId ?? null,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
